import * as echarts from '../vendors/echarts.esm.min.js';
import { numberFormat } from './Utils.js';

// To self-host ECharts, replace the import above with your local module URL.
const TYPES = new Set(['line', 'area', 'bar', 'horizontalBar', 'pie', 'donut', 'treemap', 'custom']);
const CARTESIAN = new Set(['line', 'area', 'bar', 'horizontalBar']);
const owners = new WeakMap();
const plain = value => value !== null && typeof value === 'object'
    && Object.getPrototypeOf(value) === Object.prototype;

// Arrays replace rather than concatenate. Functions (formatters) are preserved.
function merge(base, patch) {
    const result = { ...base };
    for (const [key, value] of Object.entries(patch)) {
        if (['__proto__', 'constructor', 'prototype'].includes(key)) continue;
        result[key] = plain(value)
            ? merge(plain(result[key]) ? result[key] : {}, value)
            : Array.isArray(value) ? value.map(copy) : value;
    }
    return result;
}
function copy(value) {
    return plain(value) ? merge({}, value) : Array.isArray(value) ? value.map(copy) : value;
}

const defaults = {
    type: 'line', categories: [], series: [], sparkline: false,
    smooth: false, stack: false, legend: true, tooltip: true,
    colors: ['#50C3F7', '#FFB454', '#68D6A0', '#B69CFF', '#FF7D91'],
    textColor: '#F1F1F1', mutedColor: '#A3A7AA', gridColor: '#3A3D3F',
    backgroundColor: 'transparent', animation: true,
    valueFormatter: value => document.body.classList.contains('privacy-mode')
        ? '***'
        : numberFormat(value),
    options: {},
};

/**
 * One wrapper per container. Constructor's third argument is ECharts init options.
 * CSS must provide a nonzero height. Hidden containers initialize when visible.
 * Preset series: [{ id?, name, data: [number|null], color?, options? }].
 * Pie/donut uses exactly one series; categories become slice names.
 */
export default class Charts {
    #element;
    #config;
    #initOptions;
    #chart = null;
    #observer = null;
    #frame = null;
    #disposed = false;
    #events = new Map();
    #loading = null;
    #schedule = () => {
        if (this.#disposed || this.#frame !== null) return;
        this.#frame = requestAnimationFrame(() => {
            this.#frame = null;
            this.resize();
        });
    };

    constructor(target, config = {}, initOptions = {}) {
        this.#element = typeof target === 'string' ? document.querySelector(target) : target;
        if (!(this.#element instanceof HTMLElement)) throw new Error('Charts: container not found.');
        if (owners.has(this.#element) || echarts.getInstanceByDom(this.#element)) {
            throw new Error('Charts: container already has a chart; update or destroy it first.');
        }
        this.#config = merge(defaults, config);
        this.#validate(this.#config);
        this.#initOptions = { renderer: 'canvas', ...initOptions };
        owners.set(this.#element, this);
        try {
            this.resize();
            if (typeof ResizeObserver !== 'undefined') {
                this.#observer = new ResizeObserver(this.#schedule);
                this.#observer.observe(this.#element);
            }
            window.addEventListener('resize', this.#schedule);
        } catch (error) {
            this.destroy();
            throw error;
        }
    }

    #assertAlive() {
        if (this.#disposed) throw new Error('Charts: instance has been destroyed.');
    }

    #validate(config) {
        if (!TYPES.has(config.type)) throw new Error(`Charts: unsupported preset "${config.type}".`);
        if (config.sparkline && !CARTESIAN.has(config.type)) {
            throw new Error('Charts: sparkline requires line, area, bar or horizontalBar.');
        }
        if (!Array.isArray(config.categories) || !Array.isArray(config.series)) {
            throw new TypeError('Charts: categories and series must be arrays.');
        }
        if (typeof config.valueFormatter !== 'function') throw new TypeError('Charts: valueFormatter must be a function.');
        if (config.type === 'custom') return;
        if (['pie', 'donut'].includes(config.type) && config.series.length > 1) {
            throw new Error('Charts: pie/donut requires at most one series. Select a series explicitly.');
        }
        const ids = new Set();
        config.series.forEach((series, index) => {
            if (!plain(series) || !Array.isArray(series.data)) throw new TypeError('Charts: every series needs a data array.');
            const id = String(series.id ?? `series-${index}`);
            if (ids.has(id)) throw new Error(`Charts: duplicate series id "${id}".`);
            ids.add(id);
            if (series.data.length !== config.categories.length) {
                throw new Error('Charts: each series must have one value per category.');
            }
            if (series.data.some(value => value !== null && (typeof value !== 'number' || !Number.isFinite(value)))) {
                throw new TypeError('Charts: preset data must contain finite numbers or null.');
            }
            if (['pie', 'donut'].includes(config.type) && series.data.some(value => value !== null && value < 0)) {
                throw new Error('Charts: pie/donut values cannot be negative.');
            }
        });
    }

    #build() {
        const c = this.#config;
        const spark = c.sparkline;
        const pie = c.type === 'pie' || c.type === 'donut';
        const horizontal = c.type === 'horizontalBar';
        const bar = c.type === 'bar' || horizontal;
        const base = {
            backgroundColor: c.backgroundColor,
            color: c.colors,
            textStyle: { color: c.textColor, fontFamily: 'inherit'},
            animation: c.animation,
            tooltip: { show: c.tooltip, trigger: pie ? 'item' : 'axis', renderMode: 'richText', confine: true, valueFormatter: c.valueFormatter, backgroundColor: '#252728', borderColor: 'rgba(255, 255, 255, 0.08)', textStyle: {color: c.textColor, fontSize: 12}},
            legend: { show: c.legend && !spark, type: 'scroll', bottom: 0,
                textStyle: { color: c.textColor } },
        };
        if (c.type === 'custom') return merge(base, c.options);
        if (pie) {
            base.series = c.series.map((s, index) => merge({
                id: String(s.id ?? `series-${index}`), name: s.name ?? '', type: 'pie',
                radius: c.type === 'donut' ? ['48%', '72%'] : '72%',
                center: ['50%', c.legend ? '44%' : '50%'],
                label: { show: false }, labelLine: { show: false },
                data: s.data.map((value, i) => ({ name: String(c.categories[i]), value }))
                    .filter(item => item.value !== null),
            }, s.options ?? {}));
        } else {
            const categoryAxis = {
                type: 'category',
                show: !spark,
                data: c.categories,
                boundaryGap: bar,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: c.mutedColor,
                    hideOverlap: true,
                    ...(!horizontal ? {
                        alignMinLabel: 'left',
                        alignMaxLabel: 'right',
                    } : {}),
                },
            };
            const valueAxis = { type: 'value', show: !spark,
                axisLabel: {
                    color: c.mutedColor,
                    formatter: value => {
                        if (document.body.classList.contains('privacy-mode')) {
                            return '***';
                        }

                        const number = Number(value);
                        const abs = Math.abs(number);

                        if (abs >= 1_000_000) {
                            return `${Number((number / 1_000_000).toFixed(1))}M`;
                        }

                        if (abs >= 1_000) {
                            return `${Number((number / 1_000).toFixed(1))}k`;
                        }

                        return `${number}`;
                    }
                },
                splitLine: { show: !spark, lineStyle: { color: c.gridColor } }};
            base.grid = spark ? { top: 5, right: 5, bottom: 5, left: 5, containLabel: false }
                : { top: 16, right: 16, bottom: c.legend ? 44 : 12, left: 12, containLabel: true };
            base.xAxis = horizontal ? valueAxis : categoryAxis;
            base.yAxis = horizontal ? { ...categoryAxis, inverse: true } : valueAxis;
            base.series = c.series.map((s, index) => {
                const series = {
                    id: String(s.id ?? `series-${index}`), name: s.name ?? '',
                    type: bar ? 'bar' : 'line', data: s.data,
                    ...(c.stack ? { stack: typeof c.stack === 'string' ? c.stack : 'total' } : {}),
                    ...(s.color ? { itemStyle: { color: s.color } } : {}),
                };
                if (bar) series.barMaxWidth = spark ? 12 : 40;
                else Object.assign(series, { smooth: c.smooth, showSymbol: false,
                    lineStyle: { width: spark ? 2 : 3, ...(s.color ? { color: s.color } : {}) },
                    ...(c.type === 'area' ? { areaStyle: { opacity: 0.16 } } : {}) });
                return merge(series, s.options ?? {});
            });
        }
        // Native options have final precedence, including over sparkline defaults.
        return merge(base, c.options);
    }

    #render() {
        const options = this.#build();

        const applyPrivacy = (items) => {
            for (const item of items) {
                if (item.style?.privacyText !== undefined) {
                    item.style.text = document.body.classList.contains('privacy-mode')
                        ? item.style.privacyText
                        : item.style.text;

                    delete item.style.privacyText;
                }

                if (item.children) {
                    applyPrivacy(item.children);
                }
            }
        };

        if (options.graphic) {
            applyPrivacy(
                Array.isArray(options.graphic)
                    ? options.graphic
                    : [options.graphic]
            );
        }

        this.#chart.setOption(options, { notMerge: true });
    }

    /** Merge wrapper config; arrays replace. Native `options` deep-merge. */
    update(patch = {}) {
        this.#assertAlive();
        const next = merge(this.#config, patch);
        this.#validate(next);
        this.#config = next;
        if (this.#chart) this.#render();
        else this.resize();
        return this;
    }

    setData({ categories = this.#config.categories, series = this.#config.series } = {}) {
        return this.update({ categories, series });
    }

    /** Native overrides and per-series options persist: clear them if type-specific. */
    setType(type, patch = {}) {
        return this.update({ type, ...(!CARTESIAN.has(type) ? { sparkline: false } : {}), ...patch });
    }

    setSparkline(enabled = true) { return this.update({ sparkline: Boolean(enabled) }); }

    /** Replace ALL native overrides. Use update({ options }) to merge instead. */
    setOptions(options = {}) {
        this.#assertAlive();

        if (!plain(options)) {
            throw new TypeError('Charts: options must be an object.');
        }

        this.#config.options = merge(
            this.#config.options,
            copy(options)
        );

        if (this.#chart) {
            this.#render();
        }

        return this;
    }

    setLegend(show = true) {
        this.#assertAlive();

        this.#config.legend = show;

        if (this.#chart) {
            this.#render();
        }

        return this;
    }

    resize() {
        this.#assertAlive();
        if (!this.#element.clientWidth || !this.#element.clientHeight) return this;
        if (!this.#chart) {
            this.#chart = echarts.init(this.#element, null, this.#initOptions);
            this.#render();
            for (const [event, handlers] of this.#events) {
                for (const handler of handlers) this.#chart.on(event, handler);
            }
            if (this.#loading) this.#chart.showLoading('default', this.#loading);
        } else this.#chart.resize();
        return this;
    }

    on(event, handler) {
        this.#assertAlive();
        if (typeof handler !== 'function') throw new TypeError('Charts: handler must be a function.');
        const handlers = this.#events.get(event) ?? new Set();
        if (!handlers.has(handler)) this.#chart?.on(event, handler);
        handlers.add(handler);
        this.#events.set(event, handlers);
        return this;
    }

    off(event, handler) {
        this.#assertAlive();
        const handlers = this.#events.get(event);
        if (handler) { handlers?.delete(handler); this.#chart?.off(event, handler); }
        else { this.#events.delete(event); this.#chart?.off(event); }
        return this;
    }

    showLoading(options = {}) {
        this.#assertAlive();
        this.#loading = { color: this.#config.colors[0], textColor: this.#config.textColor,
            maskColor: 'rgba(28,28,29,0.8)', ...options };
        this.#chart?.showLoading('default', this.#loading);
        return this;
    }

    hideLoading() {
        this.#assertAlive();
        this.#loading = null;
        this.#chart?.hideLoading();
        return this;
    }

    /** Native escape hatch; null while the container has never been visible. */
    get instance() { return this.#chart; }

    destroy() {
        if (this.#disposed) return;
        this.#observer?.disconnect();
        window.removeEventListener('resize', this.#schedule);
        if (this.#frame !== null) cancelAnimationFrame(this.#frame);
        this.#chart?.dispose();
        this.#chart = null;
        this.#events.clear();
        owners.delete(this.#element);
        this.#disposed = true;
    }
}
